import { useDisclosure } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreditAndRebillReqSchema,
  CustomFieldEntityEnum,
  CustomFieldRecordSchema,
  EditInvoiceFormSchema,
  EditInvoiceFormValues,
  IBillGroupResp,
  IContactRespSchema,
  IInvoiceRespSchema,
  InvoiceStatusEnum,
  InvoiceTypes,
  InvoiceUpdateSchema,
  OneTimeInvoicePatchSchema,
  OneTimeInvoicePostDraftPatchSchema,
  QuoteSettingsDefaultAddressSourceEnum,
} from '@monetize/types/app';
import { useQueryClient } from '@tanstack/react-query';
import pick from 'lodash/pick';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useAtomValue } from 'jotai';
import {
  useGetAccountById,
  useSetContactOnInvoice,
  useUpdateInvoiceCustomFields,
} from '../../api/accountsService';
import { handleApiErrorToast } from '../../api/axios';
import { doGetContactById } from '../../api/contactsService';
import {
  useCreateCreditAndRebill,
  useSetAddressOnInvoice,
  useUpdateInvoiceDetails,
  useUpdateOneTimeInvoice,
} from '../../api/invoiceService';
import { useGetById } from '../../api/queryUtils';
import { AccountAddressSelect } from '../../components/Account/AccountAddressSelect';
import { ContactFormModal } from '../../components/Contacts/ContactFormModal';
import { ContactSelect } from '../../components/Contacts/ContactSelect';
import { CustomFieldDataForm } from '../../components/CustomFields/CustomFieldDataForm';
import {
  MAlert,
  MButton,
  MCenterModal,
  MDivider,
  MFormField,
  MGrid,
  MGridItem,
  MInput,
  MLockedTextOrContent,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
  TemplateExpressionHelp,
} from '../../components/Monetize';
import { DatePicker } from '../../components/Monetize/DatePicker/DatePicker';
import { useCustomSelectValue } from '../../components/Monetize/MCustomSelect/useCustomSelectValue';
import MEditor from '../../components/Monetize/MEditor';
import { TemplateExpressionWarning } from '../../components/Monetize/TemplateExpressionWarning';
import { MTooltip } from '../../components/Monetize';
import { ROUTES } from '../../constants';
import { useACL } from '../../services/acl/acl';
import { logger } from '../../services/logger';
import { appGlobalDataState } from '../../store/global.store';
import { getAddressFromContact } from '../../utils/address';
import { CreditAndRebillApplyCheckbox } from './CreditAndRebillApplyCheckbox';
import {
  buildInvoiceAddressPayload,
  buildInvoiceContactPayload,
  buildOneTimeInvoicePatchPayload,
  buildOneTimeInvoicePostDraftPatchPayload,
  hasDirtyAddressFields,
  hasDirtyContactFields,
  isInvoiceHeaderReadOnly,
} from './EditInvoiceModal.utils';
import { EditUnpaidInvoiceContactForm } from './EditUnpaidInvoiceContactForm';

interface EditInvoiceModalProps {
  isDraftInvoice?: boolean;
  invoice: IInvoiceRespSchema;
  isOpen: boolean;
  enableCreditAndRebill: boolean;
  isCreditAnRebillPossible: boolean;
  handleClose: () => void;
}
export const EditInvoiceModal = ({
  isDraftInvoice,
  invoice,
  isOpen,
  enableCreditAndRebill,
  isCreditAnRebillPossible,
  handleClose,
}: EditInvoiceModalProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canDo } = useACL();
  const canUpdateContact = canDo([['account_contacts', 'update']]);
  const canCreateContact = canDo([['account_contacts', 'create']]);
  const contactModalState = useDisclosure();
  const { hasCrmConfigured } = useAtomValue(appGlobalDataState);
  const [creditAndRebill] = useState<boolean>(enableCreditAndRebill);
  const [applyCreditNote, setApplyCreditNote] = useState(enableCreditAndRebill);
  const [contactId, setContactId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { isLoading: isBillGroupLoading } = useGetById<IBillGroupResp>(
    'billGroups',
    invoice.billGroupId,
    {
      enabled: !!invoice.billGroupId,
      refetchOnWindowFocus: false,
    },
  );
  const accountId = invoice.account.id;
  const { data: accountDetails } = useGetAccountById(accountId, {
    enabled: !!accountId,
    refetchOnWindowFocus: false,
  });
  const creditAndRebillResData = pick(
    invoice,
    Object.keys(CreditAndRebillReqSchema.shape),
  );
  const isFinalizedInvoiceMode = !isDraftInvoice && !creditAndRebill;
  const isOneTimeInvoice = invoice.invoiceType === InvoiceTypes.ONETIME;

  const accountAddresses: string[] = [];

  if (accountDetails?.billingAddress?.id) {
    accountAddresses.push(accountDetails?.billingAddress?.id);
  }

  if (accountDetails?.shippingAddress?.id) {
    accountAddresses.push(accountDetails?.shippingAddress?.id);
  }

  const getDefaultAddressSource = (invoice: IInvoiceRespSchema) => {
    if (
      accountAddresses.includes(invoice.billingAddress?.id || '') ||
      accountAddresses.includes(invoice.shippingAddress?.id || '')
    ) {
      return QuoteSettingsDefaultAddressSourceEnum.ACCOUNT;
    } else {
      return QuoteSettingsDefaultAddressSourceEnum.CONTACT;
    }
  };

  const defaultValues: EditInvoiceFormValues = {
    ...creditAndRebillResData,
    addressInformation: {
      addressSource: getDefaultAddressSource(invoice),
      billingAddressId: accountAddresses.includes(
        invoice.billingAddress?.id || '',
      )
        ? invoice.billingAddress?.id
        : undefined,
      shippingAddressId: accountAddresses.includes(
        invoice.shippingAddress?.id || '',
      )
        ? invoice.shippingAddress?.id
        : undefined,
    },
    billingContactId: invoice.billingContact.id,
    shippingContactId: invoice.shippingContact.id,
    applyCreditNote,
    customFields: creditAndRebillResData.customFields || {},
    invoiceMemo: invoice?.invoiceMemo ?? '',
    dueDate: invoice.dueDate,
    invoicingDate: invoice.invoicingDate,
    invoiceTerms: invoice?.invoiceTerms ?? '',
  };

  const {
    watch,
    handleSubmit,
    control,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<EditInvoiceFormValues>({
    resolver: zodResolver(EditInvoiceFormSchema),
    mode: 'onChange',
    defaultValues,
    values: defaultValues,
  });
  const { addressSource } = watch('addressInformation');
  const billingContactId = watch('billingContactId');
  const shippingContactId = watch('shippingContactId');
  const isAddressSourceContact =
    addressSource === QuoteSettingsDefaultAddressSourceEnum.CONTACT;
  const { mutateAsync: updateInvoiceDetailsOrCreditAndRebill } =
    useCreateCreditAndRebill();
  const { mutateAsync: updateInvoiceDetails } = useUpdateInvoiceDetails();
  const { mutateAsync: doUpdateInvoiceCustomFields } =
    useUpdateInvoiceCustomFields();
  const { mutateAsync: doUpdateInvoiceAddress } = useSetAddressOnInvoice();
  const { mutateAsync: doSetContactOnInvoice } = useSetContactOnInvoice();
  const { mutateAsync: updateOneTimeInvoice } = useUpdateOneTimeInvoice();

  const canUpdateBilling = canDo([['billing', 'update']]);
  const isReadOnly = isInvoiceHeaderReadOnly({
    status: invoice.status,
    canUpdateBilling,
    creditAndRebill,
  });
  const isReadOnlyProps = {
    isDisabled: isReadOnly,
    isReadOnly,
    variant: isReadOnly ? 'readonly' : 'outline',
  } as const;
  const isInvoiceMemoAvailable: boolean =
    creditAndRebill ||
    (invoice.status !== InvoiceStatusEnum.PAID &&
      invoice.status !== InvoiceStatusEnum.REVERSED);
  const isInvoiceTermsAvailable: boolean =
    !creditAndRebill && isInvoiceMemoAvailable;

  /**
   * Contacts and addresses are not part of any invoice header update - they
   * have their own endpoints. Shared by the one-time and recurring paths so
   * both stay in sync.
   */
  const syncContactAndAddressChanges = async (data: EditInvoiceFormValues) => {
    const addressPayload = buildInvoiceAddressPayload(data.addressInformation);

    if (hasDirtyContactFields(dirtyFields)) {
      await doSetContactOnInvoice({
        invoiceId: invoice.id,
        data: buildInvoiceContactPayload(data, addressPayload),
      });
    }
    if (hasDirtyAddressFields(dirtyFields)) {
      await doUpdateInvoiceAddress({
        invoiceId: invoice.id,
        data: addressPayload,
      });
    }
  };

  const onSubmit = async (data: EditInvoiceFormValues) => {
    try {
      setLoading(true);
      if (creditAndRebill) {
        const parsedData = CreditAndRebillReqSchema.parse(data);

        const { applyCreditToInvoiceError, creditAndRebillResponse } =
          await updateInvoiceDetailsOrCreditAndRebill({
            applyCreditToOldInvoice: applyCreditNote,
            invoice,
            payload: {
              ...parsedData,
              invoiceMemo:
                parsedData.invoiceMemo === '' ? null : parsedData.invoiceMemo,
            },
          });

        if (applyCreditToInvoiceError) {
          handleApiErrorToast(applyCreditToInvoiceError, {
            severity: 'warning',
            summary: 'Error Applying Credit Note',
          });
        }

        if (creditAndRebillResponse.invoice.id) {
          navigate(
            {
              pathname: ROUTES.getInvoiceDetailRoute(
                creditAndRebillResponse.invoice.id,
              ),
            },
            { replace: true },
          );
        }
      } else {
        const {
          customFields,
          purchaseOrderNumber,
          registrationNumber,
          vatNumber,
          invoiceMemo,
          invoiceTerms,
        } = data;
        if (isReadOnly) {
          const parsedCustomFields =
            CustomFieldRecordSchema.parse(customFields);
          await doUpdateInvoiceCustomFields({
            invoiceId: invoice.id,
            customFields: parsedCustomFields,
          });
        } else if (isOneTimeInvoice) {
          // Once out of DRAFT the API only accepts a subset of header fields.
          const parsedPatchPayload = isDraftInvoice
            ? OneTimeInvoicePatchSchema.parse(
                buildOneTimeInvoicePatchPayload(invoice, data),
              )
            : OneTimeInvoicePostDraftPatchSchema.parse(
                buildOneTimeInvoicePostDraftPatchPayload(data),
              );
          await updateOneTimeInvoice({
            invoiceId: invoice.id,
            data: parsedPatchPayload,
          });
          // Draft invoices carry contacts and addresses in the PATCH above;
          // post-draft they are immutable there and need their own endpoints.
          if (!isDraftInvoice) {
            await syncContactAndAddressChanges(data);
          }
        } else {
          const parsedInvoiceData = InvoiceUpdateSchema.parse({
            customFields,
            purchaseOrderNumber,
            registrationNumber,
            vatNumber,
            invoiceMemo,
            invoiceTerms,
          });
          await updateInvoiceDetails({
            invoiceId: invoice.id,
            data: parsedInvoiceData,
          });

          if (
            !isFinalizedInvoiceMode ||
            invoice.status === InvoiceStatusEnum.UNPAID
          ) {
            await syncContactAndAddressChanges(data);
          }
        }
      }
    } catch (err) {
      handleApiErrorToast(err);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const onError = (err: any) =>
    logger.error('Error while updating invoice details ====>', err);

  const billingContactCustomValue = useCustomSelectValue<IContactRespSchema>({
    value: billingContactId,
    setValue: (val) => {
      setValue('billingContactId', val, { shouldDirty: true });
    },
    getOneById: doGetContactById,
  });

  const billingAddressTextForAddressSourceContact =
    billingContactCustomValue.internalValue
      ? getAddressFromContact(billingContactCustomValue.internalValue, {
          addressFormat: invoice.fromCompany.addressFormat,
        }).fullAddress
      : 'Address populates from Billing Contact';

  const shippingContactCustomValue = useCustomSelectValue<IContactRespSchema>({
    value: shippingContactId,
    setValue: (val) => {
      setValue('shippingContactId', val, { shouldDirty: true });
    },
    getOneById: doGetContactById,
  });

  const shippingAddressTextForAddressSourceContact =
    shippingContactCustomValue.internalValue
      ? getAddressFromContact(shippingContactCustomValue.internalValue, {
          addressFormat: invoice.fromCompany.addressFormat,
        }).fullAddress
      : 'Address populates from Billing Contact';

  const handleEdit = async (contactId?: string) => {
    if (contactId) {
      setContactId(contactId);
      contactModalState.onOpen();
    }
  };

  return (
    <>
      <MCenterModal
        size="full"
        isLoading={isBillGroupLoading}
        isOpen={isOpen}
        onClose={handleClose}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        modalTitle={enableCreditAndRebill ? 'Credit & Rebill' : 'Edit Invoice'}
        modalOverlayProps={{ zIndex: '100' }}
        modalPositionerProps={{ zIndex: '100' }}
        modalFooterProps={{ border: 0 }}
        renderFooter={() => (
          <MGrid templateColumns="repeat(1, 1fr)" width={'100%'} gap={2}>
            <MGridItem>
              {!creditAndRebill && !isFinalizedInvoiceMode && (
                <MAlert type="warning">
                  <MText>
                    Tax is not automatically recalculated upon save.
                  </MText>
                </MAlert>
              )}
              {creditAndRebill && (
                <CreditAndRebillApplyCheckbox
                  invoice={invoice}
                  applyCreditNote={applyCreditNote}
                  onApplyCreditNote={setApplyCreditNote}
                />
              )}
            </MGridItem>
            <MGridItem>
              <MStack
                gap={4}
                direction="row"
                align="center"
                justify="right"
                flex={1}
              >
                <MButton variant="cancel" onClick={handleClose} minW="auto">
                  Cancel
                </MButton>
                <MButton
                  form="edit-invoice-form"
                  variant="primary"
                  isLoading={
                    loading ||
                    isBillGroupLoading ||
                    billingContactCustomValue.isLoading ||
                    shippingContactCustomValue.isLoading
                  }
                  disabled={
                    loading ||
                    isBillGroupLoading ||
                    billingContactCustomValue.isLoading ||
                    shippingContactCustomValue.isLoading
                  }
                  type="submit"
                  minW="auto"
                >
                  {/* {creditAndRebill ? 'Preview' : 'Save'} */}
                  Save
                </MButton>
              </MStack>
            </MGridItem>
          </MGrid>
        )}
      >
        <form id="edit-invoice-form" onSubmit={handleSubmit(onSubmit, onError)}>
          {isCreditAnRebillPossible &&
            invoice.status !== InvoiceStatusEnum.DRAFT && (
              <>
                {!isFinalizedInvoiceMode && (
                  <MText>
                    Credit Note will be created for the full value of this
                    Invoice. Then, changes made here will be reflected in a new
                    (rebilled) Invoice.
                  </MText>
                )}

                {!isFinalizedInvoiceMode && <MDivider my={4} />}
              </>
            )}

          {!isFinalizedInvoiceMode && (
            <MFormField
              label="Addresses Sourced From"
              isHorizontal
              mb={4}
              labelProps={{ mb: 0 }}
              tooltip="Set source of Shipping and Billing Addresses on this Invoice"
            >
              <Controller
                control={control}
                name="addressInformation.addressSource"
                render={({ field: { value, onChange, ...rest } }) => (
                  <MRadioGroup
                    onChange={onChange}
                    value={value}
                    {...rest}
                    isDisabled={isReadOnlyProps.isDisabled}
                  >
                    <MStack gap={4} direction="row" color="tGray.darkPurple">
                      <MRadio
                        value={QuoteSettingsDefaultAddressSourceEnum.ACCOUNT}
                        isDisabled={!hasCrmConfigured}
                        onChange={() => {
                          setValue(
                            'addressInformation.billingAddressId',
                            undefined,
                          );
                          setValue(
                            'addressInformation.shippingAddressId',
                            undefined,
                          );
                        }}
                      >
                        <MTooltip
                          label="CRM must be connected to use Account Addresses"
                          placement="bottom-start"
                          shouldWrapChildren
                          isDisabled={hasCrmConfigured}
                        >
                          <MText
                            display="inline-flex"
                            alignItems="center"
                            color="currentColor"
                          >
                            Account Addresses
                          </MText>
                        </MTooltip>
                      </MRadio>
                      <MRadio
                        value={QuoteSettingsDefaultAddressSourceEnum.CONTACT}
                        onChange={() => {
                          setValue(
                            'addressInformation.billingAddressId',
                            undefined,
                          );
                          setValue(
                            'addressInformation.shippingAddressId',
                            undefined,
                          );
                        }}
                      >
                        Contacts
                      </MRadio>
                    </MStack>
                  </MRadioGroup>
                )}
              />
            </MFormField>
          )}

          {!isFinalizedInvoiceMode && (
            <>
              {/* Column flow keeps the single-column order (contact, address,
                  contact, address) while pairing contacts on row 1 and
                  addresses on row 2 once there is room for two columns. */}
              <MGrid
                templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }}
                templateRows={{ xl: 'repeat(2, auto)' }}
                autoFlow={{ base: 'row', xl: 'column' }}
                alignItems="start"
                gap={4}
                mb={4}
              >
                <MFormField
                  isRequired
                  label="Billing Contact"
                  labelRightComponent={
                    canUpdateContact &&
                    billingContactCustomValue.internalValue && (
                      <MButton
                        size="xs"
                        mb="2"
                        onClick={() => {
                          handleEdit(
                            billingContactCustomValue.internalValue?.id,
                          );
                        }}
                        {...isReadOnlyProps}
                        variant="tertiary"
                      >
                        Edit
                      </MButton>
                    )
                  }
                >
                  <ContactSelect
                    showAddContacts={canCreateContact}
                    accountId={accountId}
                    loading={billingContactCustomValue.isLoading}
                    value={billingContactCustomValue.internalValue}
                    returnItem
                    onChange={(val) => {
                      billingContactCustomValue.onInternalValueChange(
                        val as IContactRespSchema,
                      );
                    }}
                    clearable={false}
                    {...isReadOnlyProps}
                  />
                </MFormField>

                <MFormField label="Billing Address">
                  <MLockedTextOrContent
                    isLocked={isAddressSourceContact}
                    text={billingAddressTextForAddressSourceContact}
                  >
                    <Controller
                      name="addressInformation.billingAddressId"
                      control={control}
                      render={({ field }) => (
                        <AccountAddressSelect
                          {...field}
                          addressFormat={invoice.fromCompany.addressFormat}
                          accountId={accountId}
                          {...isReadOnlyProps}
                        />
                      )}
                    />
                  </MLockedTextOrContent>
                </MFormField>

                <MFormField
                  isRequired
                  label="Shipping Contact"
                  labelRightComponent={
                    canUpdateContact &&
                    shippingContactCustomValue.internalValue && (
                      <MButton
                        size="xs"
                        mb="2"
                        onClick={() => {
                          handleEdit(
                            shippingContactCustomValue.internalValue?.id,
                          );
                        }}
                        {...isReadOnlyProps}
                        variant="tertiary"
                      >
                        Edit
                      </MButton>
                    )
                  }
                >
                  <ContactSelect
                    showAddContacts={canCreateContact}
                    accountId={accountId}
                    loading={shippingContactCustomValue.isLoading}
                    value={shippingContactCustomValue.internalValue}
                    returnItem
                    onChange={(val) => {
                      shippingContactCustomValue.onInternalValueChange(
                        val as IContactRespSchema,
                      );
                    }}
                    clearable={false}
                    {...isReadOnlyProps}
                  />
                </MFormField>

                <MFormField label="Shipping Address">
                  <MLockedTextOrContent
                    isLocked={isAddressSourceContact}
                    text={shippingAddressTextForAddressSourceContact}
                  >
                    <Controller
                      name="addressInformation.shippingAddressId"
                      control={control}
                      render={({ field }) => (
                        <AccountAddressSelect
                          {...field}
                          addressFormat={invoice.fromCompany.addressFormat}
                          accountId={accountId}
                          {...isReadOnlyProps}
                        />
                      )}
                    />
                  </MLockedTextOrContent>
                </MFormField>
              </MGrid>
              <MDivider my={4} />
            </>
          )}

          {invoice.status === InvoiceStatusEnum.UNPAID && !creditAndRebill && (
            <EditUnpaidInvoiceContactForm
              control={control}
              setValue={setValue}
              invoice={invoice}
              handleEdit={handleEdit}
              canUpdateContact={canUpdateContact}
              canCreateContact={canCreateContact}
              canUpdateBilling={canUpdateBilling}
              hasCrmConfigured={hasCrmConfigured}
              defaultBillingAddressId={
                accountDetails?.billingAddress?.id ?? undefined
              }
              defaultShippingAddressId={
                accountDetails?.shippingAddress?.id ?? undefined
              }
            />
          )}
          {/* Invoicing Date is only mutable while the invoice is a draft. */}
          {isOneTimeInvoice && isDraftInvoice && !creditAndRebill && (
            <MGrid
              alignItems="center"
              templateColumns="repeat(12, 1fr)"
              gap={4}
              mb={4}
            >
              <MGridItem colSpan={6}>
                <MFormField
                  error={errors.invoicingDate}
                  isRequired
                  label="Invoicing Date"
                >
                  <Controller
                    name="invoicingDate"
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <DatePicker
                        {...rest}
                        value={value ?? null}
                        onChange={(next) => onChange(next ?? '')}
                        isDisabled={isReadOnly}
                        isReadOnly={isReadOnly}
                      />
                    )}
                  />
                </MFormField>
              </MGridItem>
            </MGrid>
          )}
          <MGrid alignItems="center" templateColumns="repeat(12, 1fr)" gap={4}>
            <MGridItem colSpan={6}>
              <MFormField error={errors.purchaseOrderNumber} label="PO Number">
                <Controller
                  name="purchaseOrderNumber"
                  control={control}
                  defaultValue=""
                  render={({ field: { value, ...rest } }) => (
                    <MInput
                      placeholder="PO Number"
                      value={value || ''}
                      {...rest}
                      {...isReadOnlyProps}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={6}>
              <MFormField
                error={errors.registrationNumber}
                label="Registration Number"
              >
                <Controller
                  name="registrationNumber"
                  defaultValue=""
                  control={control}
                  render={({ field: { value, ...rest } }) => (
                    <MInput
                      placeholder="Registration Number"
                      value={value || ''}
                      {...rest}
                      {...isReadOnlyProps}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
            <MGridItem colSpan={6}>
              <MFormField error={errors.vatNumber} label="VAT Number">
                <Controller
                  name="vatNumber"
                  defaultValue=""
                  control={control}
                  render={({ field: { value, ...rest } }) => (
                    <MInput
                      placeholder="VAT Number"
                      value={value || ''}
                      {...rest}
                      {...isReadOnlyProps}
                    />
                  )}
                />
              </MFormField>
            </MGridItem>
          </MGrid>

          {isInvoiceMemoAvailable && (
            <>
              <MDivider my={4} />
              <MFormField label="Invoice Memo" mb={4}>
                <MText mb={2}>Visible to end customers</MText>
                <Controller
                  control={control}
                  name="invoiceMemo"
                  render={({ field: { value, onChange, ...fields } }) => (
                    <>
                      <MEditor
                        value={value ?? ''}
                        autoScroll
                        boxProps={{
                          pb: 0,
                          minH: 200,
                        }}
                        handleEditorChange={onChange}
                        {...fields}
                      />
                      <TemplateExpressionHelp
                        documentType="invoice"
                        testId="invoice-memo-help-btn"
                      />
                      <TemplateExpressionWarning value={value} />
                    </>
                  )}
                />
              </MFormField>
            </>
          )}

          {isInvoiceTermsAvailable && (
            <>
              <MDivider my={4} />
              <MFormField label="Invoice Terms" mb={4}>
                <MText mb={2}>Visible to end customers</MText>
                <Controller
                  control={control}
                  name="invoiceTerms"
                  render={({ field: { value, onChange, ...fields } }) => (
                    <>
                      <MEditor
                        value={value ?? ''}
                        autoScroll
                        boxProps={{
                          pb: 0,
                          minH: 200,
                        }}
                        handleEditorChange={onChange}
                        {...fields}
                      />
                      <TemplateExpressionHelp
                        documentType="invoice"
                        testId="invoice-terms-help-btn"
                      />
                      <TemplateExpressionWarning value={value} />
                    </>
                  )}
                />
              </MFormField>
            </>
          )}
          <Controller
            name="customFields"
            control={control}
            render={({ field: { value, onChange } }) => (
              <CustomFieldDataForm
                entity={CustomFieldEntityEnum.INVOICE}
                value={value}
                setValue={(val) => {
                  onChange(val);
                }}
                fieldColSpan={6}
                mode="modal"
                {...isReadOnlyProps}
                isDisabled={!canUpdateBilling}
                isReadOnly={!canUpdateBilling}
                variant={undefined}
              />
            )}
          />
        </form>
      </MCenterModal>
      {contactId && (
        <ContactFormModal
          isOpen
          accountId={accountId}
          contactId={contactId}
          onClose={(contact) => {
            contactModalState.onClose();
            setContactId(undefined);
            if (contact) {
              queryClient.invalidateQueries({
                queryKey: ['useCustomSelectValue', contact.id],
              });
            }
          }}
          onUpdatedContact={(contact) => {
            queryClient.invalidateQueries({
              queryKey: ['useCustomSelectValue', contact.id],
            });
            contactModalState.onClose();
          }}
        />
      )}
    </>
  );
};
